import { forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import './index.scss'
import Icon from "../Icon";
import Multiselect from "./Multiselect";
import Datepicker from "./Datepicker";
import DateSimple from "./DateSimple";
import Daterange from "./Daterange";
import { ICON } from "../Icon/data";
import ButtonSelect from "./ButtonSelect";
import Select from "./Select";
import useDynamicRef from "../../hooks/useDynamicRef";
import Toggle from "./Toggle";
import { Input as InputType } from "./types";
import CheckboxMultiselect from "./CheckboxMultiselect";

const Input = memo(forwardRef(({
  type, label, value, placeholder, size = 'l', onInput = () => {}, text,
  error, options = {}, disabled = false, readonly = false, searchable = false, name = '',
  valueKey = '', onFocus, format, onBlur, dateKey, onKeyPress, hideLabel = false,
}: InputType, ref) => {
  const inputRef = useRef()

  const setFocus = useCallback(() => {
    inputRef?.current?.focus()
  }, [inputRef])

  const forwardRefAction = useDynamicRef({
    focus: setFocus,
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  const classNames = useMemo(() => {
    const classArr = [`input-${type}`, `size-${size}`]
    if (error?.type) {
      classArr.push(error?.type)
    }

    return classArr.join(' ')
  }, [size, type, error])

  const inputElem = useMemo(() => {
    const params = {
      ref: inputRef,
      type,
      id: label ?? valueKey,
      name: label ?? valueKey,
      value,
      disabled,
      placeholder,
      onChange: (e: Event) => onInput(e.target?.value),
      dateKey,
      size
    }

    if (readonly) {
      if (type === 'checkbox') {
        return (
          <div className="Input__checkbox-readonly">
            <input
              {...params}
              value={valueKey}
              checked={value}
              disabled={true}
              onChange={e => {}}
            />
          </div>
        )
      } else if (type === 'select') {
        return (
          <div className="Input__input-readonly">{options[value]}</div>
        )
      }

      return (
        <div className="Input__input-readonly">{value}</div>
      )
    }

    if (type === 'number') { // Make sure that type number returns a number, not string!
      params.onChange = (e: Event) => {
        const number = Number(e.target?.value)
        onInput(number)
      }
    }
    if (onFocus) params.onFocus = () => onFocus()
    if (onBlur) params.onBlur = () => onBlur()
    if (onKeyPress) params.onKeyPress = (e: Event) => onKeyPress(e)
    if (format) params.format = format

    switch (type) {
      case 'text':
      case 'password':
      case 'number':
        return (
          <input {...params} />
        )
        break
      
      case 'select':
        return (
          <Select
            {...params}
            searchable={searchable}
            options={options}
            onChange={e => onInput(e)}
          />
        )
        break
      
      case 'buttonselect':
        return (
          <ButtonSelect
            {...params}
            label={label}
            options={options}
            onChange={e => onInput(e)}
          />
        )
        break

      case 'checkbox':
        return (
          <div className="input-checkbox__focus">
            <input
              {...params}
              value={valueKey}
              checked={value}
              onChange={e => onInput(e.target.checked)}
            />
          </div>
        )
        break

      case 'toggle':
        return (
          <div className="input-toggle__focus">
            <Toggle
              {...params}
              toggled={value}
              onInput={(val) => onInput(val)}
            />
          </div>
        )
      
      case 'radio':
        return (
          <input
            {...params}
            checked={value === valueKey}
            name={name}
            value={valueKey}
            onChange={e => onInput(e.target.value)}
          />
        )
        break
      
      case 'multiselect':
        return (
          <Multiselect
            {...params}
            options={options}
            onChange={e => onInput(e)}
          />
        )
        break

      case 'checkboxmultiselect':
        return (
          <CheckboxMultiselect
            {...params}
            options={options}
            onChange={e => onInput(e)}
          />
        )
        break
      
      case 'datesimple':
        return (
          <DateSimple
            {...params}
            onChange={e => onInput(e)}
          />
        )

      case 'date':
        return (
          <Datepicker
            {...params}
            onChange={e => onInput(e)}
          />
        )
        break

      case 'daterange':
        if (!hideLabel) params.hideLabel = hideLabel
        return (
          <Daterange
            {...params}
            onChange={e => onInput(e)}
          />
        )
        break

      case 'textarea':
        return (
          <textarea {...params} />
        )
        break

      default:
        return (
          <input
            ref={inputRef}
            type={type}
            id={label ?? valueKey}
            name={label}
            value={value}
            placeholder={placeholder}
            onChange={e => onInput(e.target.value)}
          />
        )
        break
    }
  }, [
    type, label, value, placeholder, onInput, options, readonly, searchable,
    onFocus, onBlur, format, inputRef, size, onKeyPress,
  ])

  const showLabel = label && type !== 'buttonselect' && type !== 'checkbox' && !hideLabel

  const labelElem = useMemo(() => {
    const onClick = () => {
      if (readonly) {
        return () => {}
      }

      return setFocus()
    }

    return (
      <label
        className={`Input__label${readonly ? ' readonly' : ''}`}
        htmlFor={label ?? valueKey}
        onClick={onClick}
      >
        {label}
      </label>
    )
  }, [label, valueKey, readonly, setFocus])

  return (
    <div className={`Input ${classNames}`}>
      {showLabel && labelElem}
      <div className="Input__input">
        {inputElem}
        {error?.type === 'error' && <Icon icon={ICON.ERROR} size={16} color="#D42020" />}
        {error?.type === 'warning' && <Icon icon={ICON.WARNING} size={16} color="#F1C21B" fill="#000000" />}
      </div>
      {label && (type === 'checkbox' || type === 'toggle') && labelElem}
      {!error && text && <div className="Input__text">{text}</div>}
      {error && <div className={`Input__text ${error?.type}`}>{error?.message}</div>}
    </div>
  );
}))

export default Input;
